import React, { SyntheticEvent } from 'react'
import { format, parseISO } from 'date-fns'
import { th } from 'date-fns/locale'
import { isEmpty } from 'lodash'

import { PriorityLabel, Tag, Skeleton } from 'components'
import { Row, TBody, ActionEventTable } from 'components/Table'
import { DATE } from 'helpers/constants'
import { HEADERS_LOADING } from 'features/Ticket'
import ReactTooltip from 'react-tooltip'

import type { IColumn, Ticket } from 'types'

type PropsTicketList = {
  columns: IColumn[]
  tickets: Ticket[]
  showMenu?: boolean
  loading: boolean
  isError?: boolean
  onClickRow: (id: string) => (e: SyntheticEvent) => void
}

const SkeletonListOdd: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='214px' />,
  requestDate: <Skeleton.whiteSkeleton width='171px' />,
  branchName: <Skeleton.whiteSkeleton width='214px' />,
  area: <Skeleton.whiteSkeleton width='171px' />,
}

const SkeletonListEven: Record<string, React.ReactNode> = {
  no: <Skeleton.whiteSkeleton width='214px' />,
  requestDate: <Skeleton.whiteSkeleton width='149px' />,
  branchName: <Skeleton.whiteSkeleton width='214px' />,
  area: <Skeleton.whiteSkeleton width='149px' />,
}

export const ProductNameCell = ({
  ticket,
}: {
  ticket: Ticket
}): JSX.Element | null => {
  const base = 'con-ticket'
  if (!ticket.products || ticket.products.length === 0) {
    return null
  }

  const firstProduct = ticket.products[0]
  const remainingProductsCount = ticket.products.length - 1

  return (
    <div>
      <span className={`${base}__cell-body-2-line`}>
        {firstProduct.name}
        {remainingProductsCount > 0 && `, +${remainingProductsCount}`}
      </span>
    </div>
  )
}

export const TicketList: React.FC<PropsTicketList> = (props) => {
  const { columns, isError = false, loading, onClickRow, tickets } = props
  const base = 'con-ticket'

  const list: Record<string, React.ReactNode>[] = tickets.map((ticket) => {
    return {
      code: ticket.code,
      name: ticket.products[0].name && <ProductNameCell ticket={ticket} />,
      issueDetail: ticket.issueDetail && (
        <>
          <span
            className={`${base}__cell-body-3-line`}
            data-tip={ticket.issueDetail}
            data-for='tooltip'
          >
            {ticket.issueDetail}
          </span>
          <ReactTooltip
            className={`${base}__cell-tooltip`}
            place='top'
            type='dark'
            id='tooltip'
          />
        </>
      ),
      statusProgress: ticket.statusProgress && (
        <div className={`${base}__cell-body-wrapper`}>
          <span className={`${base}__cell-body-text`}>
            <Tag status={ticket.statusProgress} />
          </span>
        </div>
      ),
      requestDate: format(parseISO(ticket.requestDate), DATE.LOCAL_MONTH, {
        locale: th,
      }),
      type: ticket.type.nameTh,
      statusPriority: ticket.statusPriority && (
        <div className={`${base}__cell-body-wrapper`}>
          <span className={`${base}__cell-body-text`}>
            <PriorityLabel
              className={`${base}__priority-label`}
              status={ticket.statusPriority}
            />
          </span>
        </div>
      ),
      companyName: ticket.companyName && (
        <span className={`${base}__cell-body-2-line`}>
          {ticket.companyName}
        </span>
      ),
      branchName: ticket.branchName && (
        <span className={`${base}__cell-body-2-line`}>{ticket.branchName}</span>
      ),
    }
  })
  if (loading) {
    return (
      <Skeleton.SkeletonList
        columns={HEADERS_LOADING}
        rowOdd={SkeletonListOdd}
        rowEven={SkeletonListEven}
      />
    )
  }
  if (isEmpty(tickets) || isError) {
    return <ActionEventTable colSpan={columns.length} isNotFound />
  }

  return (
    <TBody>
      {list.map((data, index) => (
        <Row
          data-testid={`ticket-row-${tickets[index].id}`}
          key={index}
          columns={columns}
          data={data}
          onClick={onClickRow(tickets[index].id)}
          position='left'
        />
      ))}
    </TBody>
  )
}

export default TicketList
